import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Simplified SaaS Role Architecture:
// - platform_admin: Platform owner (you) - manages platform, NOT user billing data
// - super_admin: Business Owner - owns a tenant, full billing access
// Every signup = independent business owner with their own tenant
type AppRole = 'platform_admin' | 'super_admin';

// Role display names for UI
export const ROLE_DISPLAY_NAMES: Record<AppRole, string> = {
  platform_admin: 'Platform Admin',
  super_admin: 'Business Owner',
};

interface Profile {
  id: string;
  user_id: string;
  tenant_id: string | null;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  preferred_language: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  business_type: string | null;
  subscription_plan: string | null;
  subscription_status: string | null;
  subscription_expires_at: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  whatsapp_number: string | null;
  gstin: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_ifsc: string | null;
  // GST/Tax settings
  gst_enabled: boolean | null;
  gst_percentage: number | null;
  cgst_percentage: number | null;
  sgst_percentage: number | null;
  apply_gst_on_invoices: boolean | null;
  // Profile completion fields
  vle_id: string | null;
  vle_name: string | null;
  state: string | null;
  district: string | null;
  taluka: string | null;
  village: string | null;
  pincode: string | null;
  latitude: number | null;
  longitude: number | null;
  vle_photo_url: string | null;
  shop_logo_url: string | null;
  description: string | null;
  is_public: boolean | null;
  profile_completion: number | null;
  public_url_slug: string | null;
  shop_photos: string[] | null;
  event_photos: string[] | null;
  // Phase-2 fields
  profile_views: number | null;
  is_verified: boolean | null;
  working_hours: Record<string, unknown> | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  tenant: Tenant | null;
  roles: AppRole[];
  isLoading: boolean;
  isBillingInProgress: boolean;
  isSessionReady: boolean;
  // Platform admin check (separate from tenant)
  isPlatformAdmin: boolean;
  // Every business owner is a super_admin of their tenant
  isTenantOwner: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, businessName: string, mobile: string, stateCode: string, referralCode?: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  resendVerificationEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  hasRole: (role: AppRole) => boolean;
  // Simplified: canBill = has tenant (every business owner can bill)
  canBill: boolean;
  refreshSession: () => Promise<boolean>;
  setBillingInProgress: (inProgress: boolean) => void;
  ensureSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session refresh interval (every 10 minutes)
const SESSION_REFRESH_INTERVAL = 10 * 60 * 1000;

// Grace period before session expiry to trigger refresh (5 minutes)
const SESSION_EXPIRY_GRACE = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBillingInProgress, setIsBillingInProgress] = useState(false);
  
  // Ref to track if we're in the middle of refreshing
  const isRefreshing = useRef(false);
  const refreshTimer = useRef<NodeJS.Timeout | null>(null);
  const lastAuthEvent = useRef<AuthChangeEvent | null>(null);

  // Refresh session proactively
  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (isRefreshing.current) {
      console.log('[Auth] Session refresh already in progress');
      return true;
    }
    
    isRefreshing.current = true;
    console.log('[Auth] Attempting session refresh...');
    
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('[Auth] Session refresh failed:', error.message);
        isRefreshing.current = false;
        return false;
      }
      
      if (data.session) {
        console.log('[Auth] Session refreshed successfully, expires:', new Date(data.session.expires_at! * 1000).toLocaleTimeString());
        setSession(data.session);
        setUser(data.session.user);
        isRefreshing.current = false;
        return true;
      }
      
      isRefreshing.current = false;
      return false;
    } catch (err) {
      console.error('[Auth] Session refresh error:', err);
      isRefreshing.current = false;
      return false;
    }
  }, []);

  // Check if session needs refresh
  const checkAndRefreshSession = useCallback(async () => {
    if (!session) return;
    
    const expiresAt = session.expires_at;
    if (!expiresAt) return;
    
    const expiresAtMs = expiresAt * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiresAtMs - now;
    
    console.log('[Auth] Time until session expiry:', Math.round(timeUntilExpiry / 1000 / 60), 'minutes');
    
    // Refresh if within grace period
    if (timeUntilExpiry < SESSION_EXPIRY_GRACE) {
      console.log('[Auth] Session expiring soon, refreshing...');
      await refreshSession();
    }
  }, [session, refreshSession]);

  // Set up periodic session check
  useEffect(() => {
    if (session) {
      // Initial check
      checkAndRefreshSession();
      
      // Set up interval
      refreshTimer.current = setInterval(checkAndRefreshSession, SESSION_REFRESH_INTERVAL);
      
      return () => {
        if (refreshTimer.current) {
          clearInterval(refreshTimer.current);
        }
      };
    }
  }, [session, checkAndRefreshSession]);

  useEffect(() => {
    console.log('[Auth] Setting up auth state listener...');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[Auth] Auth state changed:', event, newSession ? 'session exists' : 'no session');
        lastAuthEvent.current = event;
        
        // Don't clear session on TOKEN_REFRESHED - just update
        if (event === 'TOKEN_REFRESHED' && newSession) {
          console.log('[Auth] Token refreshed, new expiry:', new Date(newSession.expires_at! * 1000).toLocaleTimeString());
          setSession(newSession);
          setUser(newSession.user);
          return;
        }
        
        // Don't trigger logout during billing
        if (event === 'SIGNED_OUT' && isBillingInProgress) {
          console.log('[Auth] Ignoring SIGNED_OUT during billing, attempting refresh...');
          setTimeout(() => refreshSession(), 0);
          return;
        }
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        // Defer fetching additional data
        if (newSession?.user) {
          setTimeout(() => {
            fetchUserData(newSession.user.id);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          setTenant(null);
          setRoles([]);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      console.log('[Auth] Initial session check:', existingSession ? 'found' : 'none');
      
      if (existingSession) {
        console.log('[Auth] Session expires:', new Date(existingSession.expires_at! * 1000).toLocaleTimeString());
        setSession(existingSession);
        setUser(existingSession.user);
        fetchUserData(existingSession.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      console.log('[Auth] Cleaning up auth subscription');
      subscription.unsubscribe();
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
      }
    };
  }, []);

  const ensureTenantExists = async (userId: string, profileData: Profile): Promise<{ tenantId: string; isNew: boolean }> => {
    // If tenant already exists, return it
    if (profileData.tenant_id) {
      return { tenantId: profileData.tenant_id, isNew: false };
    }

    // Create new tenant using the secure RPC function
    // This function handles everything: tenant creation, profile update, role assignment, and settings
    const userName = profileData.full_name || 'My Business';
    const slug = userName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    
    const { data: tenantId, error: tenantError } = await supabase
      .rpc('create_tenant_for_user', {
        p_name: userName,
        p_slug: `${slug}-${Date.now()}`,
      });

    if (tenantError) throw tenantError;
    
    if (!tenantId) {
      throw new Error('Failed to create tenant - no ID returned');
    }

    return { tenantId: tenantId as string, isNew: true };
  };

  const fetchUserData = async (userId: string) => {
    try {
      console.log('[Auth] fetchUserData: Starting for user', userId);
      
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('[Auth] Error fetching profile:', profileError);
      }

      if (!profileData) {
        console.log('[Auth] No profile found, user needs setup');
        setIsLoading(false);
        return;
      }

      let finalProfile = profileData as Profile;
      console.log('[Auth] Profile loaded:', { tenant_id: finalProfile.tenant_id, name: finalProfile.full_name });

      // Auto-create tenant if missing (every user gets their own tenant)
      if (!profileData.tenant_id) {
        console.log('[Auth] No tenant_id, creating tenant...');
        try {
          const { tenantId } = await ensureTenantExists(userId, profileData as Profile);
          finalProfile = { ...finalProfile, tenant_id: tenantId };
          console.log('[Auth] Tenant created:', tenantId);
        } catch (error) {
          console.error('[Auth] Error creating tenant:', error);
        }
      }

      setProfile(finalProfile);

      // Fetch tenant
      if (finalProfile.tenant_id) {
        const { data: tenantData, error: tenantError } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', finalProfile.tenant_id)
          .single();

        if (tenantError) {
          console.error('[Auth] Error fetching tenant:', tenantError);
        }

        if (tenantData) {
          setTenant(tenantData as Tenant);
          console.log('[Auth] Tenant loaded:', tenantData.name);
        }
      }

      // Fetch roles
      console.log('[Auth] Fetching roles for user:', userId);
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      // Also check if user is a platform admin (separate table)
      const { data: platformAdminData } = await supabase
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle();

      const isPlatformAdminUser = !!platformAdminData;
      console.log('[Auth] Is platform admin:', isPlatformAdminUser);

      const userRoles: AppRole[] = [];

      // Add platform_admin if user is in platform_admins table
      if (isPlatformAdminUser) {
        userRoles.push('platform_admin');
      }

      if (rolesError) {
        console.error('[Auth] Error fetching roles:', rolesError);
        // Default to super_admin if user has tenant
        if (finalProfile.tenant_id) {
          userRoles.push('super_admin');
        }
      } else if (rolesData && rolesData.length > 0) {
        // Only accept super_admin role (ignore legacy admin/operator)
        const hasSuperAdmin = rolesData.some(r => r.role === 'super_admin');
        if (hasSuperAdmin) {
          userRoles.push('super_admin');
        } else if (finalProfile.tenant_id) {
          // Upgrade any tenant user to super_admin (migration)
          console.log('[Auth] Upgrading user to super_admin...');
          await supabase
            .from('user_roles')
            .upsert({
              user_id: userId,
              tenant_id: finalProfile.tenant_id,
              role: 'super_admin',
            }, { onConflict: 'user_id,tenant_id,role' });
          userRoles.push('super_admin');
        }
      } else if (finalProfile.tenant_id) {
        // No roles but has tenant - assign super_admin
        console.log('[Auth] No roles found, assigning super_admin...');
        await supabase
          .from('user_roles')
          .insert({
            user_id: userId,
            tenant_id: finalProfile.tenant_id,
            role: 'super_admin',
          });
        userRoles.push('super_admin');
      }

      console.log('[Auth] Final roles:', userRoles);
      setRoles(userRoles);
      
    } catch (error) {
      console.error('[Auth] Error fetching user data:', error);
      console.error('[Auth] fetchUserData error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error) {
      if (rememberMe) {
        localStorage.setItem('auth_remember_device', 'true');
        localStorage.setItem('auth_device_timestamp', Date.now().toString());
        sessionStorage.removeItem('auth_session_only');
      } else {
        sessionStorage.setItem('auth_session_only', 'true');
        localStorage.removeItem('auth_remember_device');
        localStorage.removeItem('auth_device_timestamp');
      }
    }
    
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { error: error as Error | null };
  };

  const signUp = async (
    email: string, 
    password: string, 
    fullName: string, 
    businessName: string,
    mobile: string,
    stateCode: string,
    referralCode?: string
  ) => {
    try {
      // Format mobile number with country code
      const formattedMobile = mobile ? `+91${mobile}` : null;

      // Sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('User creation failed');

      // Create tenant using the secure RPC function (bypasses RLS)
      // Now passing ALL data directly to RPC - no separate update needed!
      const slug = businessName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
      const { data: tenantId, error: tenantError } = await supabase
        .rpc('create_tenant_for_user_v2', {
          p_name: businessName,
          p_slug: `${slug}-${Date.now()}`,
          p_state_code: stateCode,
          p_referral_code: referralCode?.toUpperCase() || null,
          p_email: email,
          p_phone: formattedMobile,
          p_vle_name: fullName,
        });

      if (tenantError) {
        console.error('[Auth] create_tenant_for_user_v2 error:', tenantError);
        throw tenantError;
      }

      if (!tenantId) {
        throw new Error('Failed to create tenant - no ID returned');
      }

      console.log('[Auth] Tenant created successfully with all data:', tenantId);

      return { error: null };
    } catch (error) {
      console.error('[Auth] signUp error:', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    localStorage.removeItem('auth_remember_device');
    localStorage.removeItem('auth_device_timestamp');
    sessionStorage.removeItem('auth_session_only');
    
    
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setTenant(null);
    setRoles([]);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error: error as Error | null };
  };

  const hasRole = (role: AppRole): boolean => {
    return roles.includes(role);
  };

  // Role Helpers:
  // isPlatformAdmin: Platform owner - can manage platform, not user data
  const isPlatformAdmin = hasRole('platform_admin');
  
  // isTenantOwner: Business Owner (every logged-in user with tenant)
  const isTenantOwner = hasRole('super_admin');
  
  // canBill: Simplified - any user with a tenant can bill
  const canBill = !!profile?.tenant_id && !!tenant;

  // Session readiness check
  const isSessionReady = !isLoading && !!user && !!session && !!profile && !!profile.tenant_id && !!tenant && roles.length > 0;

  // Function to set billing in progress
  const setBillingInProgress = useCallback((inProgress: boolean) => {
    console.log('[Auth] Billing in progress:', inProgress);
    setIsBillingInProgress(inProgress);
  }, []);

  // Ensure session is valid before performing critical actions
  const ensureSession = useCallback(async (): Promise<boolean> => {
    console.log('[Auth] ensureSession called, current state:', {
      isSessionReady,
      isLoading,
      hasUser: !!user,
      hasSession: !!session,
      hasProfile: !!profile,
      hasTenant: !!tenant,
      rolesCount: roles.length
    });
    
    if (isSessionReady) {
      console.log('[Auth] ensureSession: Session already ready');
      return true;
    }
    
    if (isLoading) {
      console.log('[Auth] ensureSession: Still loading, waiting...');
      for (let i = 0; i < 6; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!isLoading && isSessionReady) {
          console.log('[Auth] ensureSession: Session became ready');
          return true;
        }
      }
    }
    
    if (user && session && (!profile || !tenant || roles.length === 0)) {
      console.log('[Auth] ensureSession: Have user but missing profile/tenant/roles, refetching...');
      await fetchUserData(user.id);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const nowReady = !!profile && !!tenant && roles.length > 0;
      if (nowReady) {
        console.log('[Auth] ensureSession: Data fetched successfully');
        return true;
      }
    }
    
    console.log('[Auth] ensureSession: Attempting session refresh...');
    const refreshed = await refreshSession();
    
    if (!refreshed) {
      console.error('[Auth] ensureSession: Session refresh failed');
      return false;
    }
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const ready = !!user && !!session && !!profile && !!tenant && roles.length > 0;
    console.log('[Auth] ensureSession: After refresh, ready:', ready);
    return ready;
  }, [isSessionReady, isLoading, refreshSession, user, session, profile, tenant, roles, fetchUserData]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        tenant,
        roles,
        isLoading,
        isBillingInProgress,
        isSessionReady,
        isPlatformAdmin,
        signIn,
        signUp,
        signInWithGoogle,
        signOut,
        resetPassword,
        resendVerificationEmail,
        updatePassword,
        hasRole,
        isTenantOwner,
        canBill,
        refreshSession,
        setBillingInProgress,
        ensureSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
